import React, { useState, useEffect } from 'react';

import Button from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import './style.scss';

interface IEmailEditModalProps {
  show: boolean;
  currentEmail: string;
  onClose: () => void;
  onSave: (newEmail: string) => void;
  isLoading?: boolean;
  error?: string;
  onErrorClear?: () => void;
}

export default function EmailEditModal({
  show,
  currentEmail,
  onClose,
  onSave,
  isLoading = false,
  error = '',
  onErrorClear
}: IEmailEditModalProps) {
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (show) {
      setNewEmail('');
      setEmailError('');
    }
  }, [show]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setNewEmail(email);
    
    if (onErrorClear && error) {
      onErrorClear();
    }
    
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleSave = () => {
    if (!newEmail.trim()) {
      setEmailError('Email address is required');
      return;
    }

    if (!validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (newEmail === currentEmail) {
      setEmailError('New email must be different from current email');
      return;
    }

    onSave(newEmail);
  };

  const handleClose = () => {
    setNewEmail('');
    setEmailError('');
    onClose();
  };

  const renderBody = () => {
    const errorToShow = emailError || error;
    
    return (
    <div className="email-edit-modal__body">
      <h3 className="email-edit-modal__title">Enter your new email address</h3>
      <div className="email-edit-modal__form">
        <Input
          field={{
            label: 'Enter Email Address',
            type: CustomFieldType.TEXT,
            value: newEmail,
            isRequired: true,
          }}
          name="newEmail"
          className="email-edit-modal__input"
          onChange={handleEmailChange}
            error={errorToShow ? { errorState: IValidationState.ERROR, errorMessage: errorToShow } : undefined}
          placeholder="Enter your new email address"
        />
        </div>
      <div className="email-edit-modal__notice">
        <p>
          Your new email address must be verified before it becomes active. 
          You will receive a verification email at the new address.
        </p>
      </div>
    </div>
  );
  };

  const renderFooter = () => (
    <div className="email-edit-modal__footer">
      <Button
        bsStyle={ButtonStyle.DEFAULT}
        onClick={handleClose}
        content="Cancel"
        disabled={isLoading}
      />
      <Button
        bsStyle={ButtonStyle.PRIMARY}
        onClick={handleSave}
        content="Save"
        disabled={isLoading || !!emailError || !newEmail.trim()}
        showLoader={isLoading}
      />
    </div>
  );

  return (
    <ModalBase
      show={show}
      onClose={handleClose}
      closeButton={true}
      titleElement="Edit Email"
      bodyElement={renderBody()}
      footerElement={renderFooter()}
      className="email-edit-modal"
      closeOnEscape={!isLoading}
      closeOnOverlayClick={!isLoading}
    />
  );
} 